/**
 * 샘플 episode 의 점검 데이터 — Worker 가 샘플과 함께 둔 artifact 를 FE 가 읽는 모양.
 *
 * **채널마다 기록된 시각 · 행 번호 · 유효성이 따로 간다.** 공통 격자에 맞추면 서로 다른 rate 와
 * 누락이 지워진다(명세 §6). 배열은 목차가 가리키는 Parquet 파일이고 여기는 그 목차와 디코드한
 * 값을 든다 — 큰 정수 ns 는 원점을 정수로 먼저 뺀 뒤 초로 바꾼다.
 *
 * Owns:
 * - 목차 · 채널 · 카메라의 값 모양, 채널 단위 시간축(초 ↔ 행) 계산.
 *
 * Does not own:
 * - wire 응답 해석 — `api/inspectApi.ts`. 배열 디코드 — `@/shared/parquet`.
 * - 재생 클럭 — c6 `useSyncedVideoTimeline`.
 */

export type ClockUnit = "s" | "ns";

/** 채널이 어느 시계로 쟀는가. `id` 가 `null` 이면 관계를 모른다 — 같은 시계라고 추정하지 않는다. */
export type InspectClockRef = {
  id: string | null;
  unit: ClockUnit;
};

/** 원본 provenance 가 밝힌 pose 의 의미. 없으면 FE 가 단위 · 회전 순서를 정하지 않는다. */
export type PoseDeclaration = {
  referenceFrame: string;
  targetFrame: string;
  positionUnit: string;
  rotation: string;
};

/** 배열 파일 하나를 가리키는 목차 항목. */
export type InspectArrayRef = {
  path: string;
  dtype: string;
  shape: readonly number[];
};

type InspectStreamBase = {
  key: string;
  clock: InspectClockRef;
  timestamps: InspectArrayRef;
  validity: InspectArrayRef;
};

export type InspectVectorStream = InspectStreamBase & {
  kind: "vector";
  values: InspectArrayRef;
  /** `null` 이면 원본 파일 행 번호 미확인 — 배열 index 로 대체하지 않는다. */
  sourceRows: InspectArrayRef | null;
  pose: PoseDeclaration | null;
};

export type InspectImageStream = InspectStreamBase & {
  kind: "image";
  sourcePath: string | null;
  /** `null` 이면 원본 frame 번호 미확인 — 반복 · 누락을 판정하지 않는다. */
  sourceFrames: InspectArrayRef | null;
  /** `null` 이면 재생 시각 대응 미확인 — 영상 위치는 합성이다. */
  presentationSeconds: InspectArrayRef | null;
  videoValidation: string;
};

export type InspectStream = InspectVectorStream | InspectImageStream;

/** 엔진이 원본을 읽으며 확인한 문제. `rows` 는 원본 배열의 위치다. */
export type InspectIssue = {
  channel: string | null;
  code: string;
  message: string;
  rows: readonly number[];
  path: string | null;
};

/**
 * 재생 클럭의 0 초. 채널마다 첫 시각을 빼서 각각 0 으로 만들지 않는다 — 그러면 원래 지연이
 * 지워진다. 고를 채널이 없으면 전부 `null` 이고 화면은 공통 시간축 없이 연다.
 */
export type InspectTimeOrigin = {
  channel: string | null;
  clockId: string | null;
  unit: ClockUnit | null;
  /** 큰 정수 ns 의 끝자리를 잃지 않게 문자열이다. */
  value: string | null;
};

/**
 * 원본이 선언한 채널 하나 — 목차가 담지 않는 차원 이름이 여기 있다.
 *
 * `names` 가 `null` 이면 원본이 열 이름을 밝히지 않았다는 뜻이다. 그때는 이름으로 축을
 * 제안하지 않는다 — 차원 수만으로 pose 를 추정하면 아닌 것을 궤적으로 그린다.
 */
export type InspectChannelDeclaration = {
  key: string;
  kind: string;
  dtype: string;
  dim: number;
  names: readonly string[] | null;
  /** 원본이 밝힌 pose 해석(좌표계 · 단위 · 회전 표현). `null` 이면 선언이 없어 사람이 정한다. */
  pose: PoseDeclaration | null;
};

/**
 * 샘플 안 mp4 하나 — 컨테이너가 밝힌 사실과 이 episode 의 초 구간.
 *
 * `url` 은 샘플에 보관된 원본 mp4 다.
 */
export type InspectVideo = {
  key: string;
  path: string;
  url: string;
  width: number;
  height: number;
  codec: string;
  fps: number;
  /** 파일 전체의 값이다(v3.0 은 여러 episode 가 mp4 하나를 나눠 쓴다). */
  durationSeconds: number;
  /** 0 이면 컨테이너 메타에 없다는 뜻이지 프레임이 0 장이라는 뜻이 아니다. */
  frameCount: number;
  declaredShape: readonly number[];
  fromSeconds: number | null;
  toSeconds: number | null;
};

/** 배열 파일 하나와 그것을 받을 URL. */
export type InspectArtifactFile = {
  path: string;
  size: number;
  sha256: string;
  url: string;
};

/** 이 화면이 읽는 목차 판. 경계는 다른 판을 계약 위반으로 거부한다. */
export const INSPECT_SCHEMA_VERSION = 2;

export type EpisodeInspect = {
  schemaVersion: number;
  sourceId: string;
  /** `raw` 는 원시값을 읽었을 뿐 recipe 적용 완료가 아니다. */
  kind: string;
  sourceFingerprint: string | null;
  engineRevision: string;
  timeOrigin: InspectTimeOrigin;
  streams: readonly InspectStream[];
  channels: readonly InspectChannelDeclaration[];
  issues: readonly InspectIssue[];
  sourceMetadata: Readonly<Record<string, unknown>>;
  videos: readonly InspectVideo[];
  artifacts: readonly InspectArtifactFile[];
  /** 파일 URL 이 만료되는 시각. 지나면 목차를 다시 받는다. */
  mediaExpiresAtMs: number;
};

/** 채널 하나의 디코드된 값 — 시각은 episode 공통 원점 기준 초다. */
export type InspectChannel = {
  key: string;
  /** 이 값을 어느 시계로 쟀는가. `null` 이면 다른 채널과의 관계가 미확인이다. */
  clockId: string | null;
  secondsFromOrigin: readonly number[];
  /** 엔진이 무효로 표시한 행. 길이가 시각과 다르면 경계가 거부한다. */
  validity: readonly boolean[];
  /** 원본 파일 행 번호. `null` 이면 미확인이다. */
  sourceRows: readonly number[] | null;
  /** vector 만. `[n][dim]` 이고 scalar 컬럼도 길이 1 행으로 편다. */
  values: readonly (readonly number[])[] | null;
  /** image 만. 재생 시각 대응이 확인된 경우에만 값이 있다. */
  presentationSeconds: readonly number[] | null;
};

export function isVectorStream(stream: InspectStream): stream is InspectVectorStream {
  return stream.kind === "vector";
}

export function isImageStream(stream: InspectStream): stream is InspectImageStream {
  return stream.kind === "image";
}

/** 원본이 선언한 채널 하나를 찾는다. */
export function findDeclaration(
  inspect: EpisodeInspect,
  key: string,
): InspectChannelDeclaration | undefined {
  return inspect.channels.find((channel) => channel.key === key);
}

/** 목차에서 채널 하나를 찾는다. */
export function findStream(inspect: EpisodeInspect, key: string): InspectStream | undefined {
  return inspect.streams.find((stream) => stream.key === key);
}

/**
 * 관측이 걸쳐 있는 전체 구간(초). 채널마다 시작 · 끝이 다르므로 가장 이른 시작부터 가장 늦은
 * 끝까지다. 표본이 없으면 0 이고 타임라인은 재생할 수 없다.
 */
export function episodeSpanSeconds(channels: readonly InspectChannel[]): number {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const channel of channels) {
    const first = channel.secondsFromOrigin[0];
    const last = channel.secondsFromOrigin[channel.secondsFromOrigin.length - 1];
    if (first === undefined || last === undefined) continue;
    min = Math.min(min, first);
    max = Math.max(max, last);
  }
  return Number.isFinite(min) && Number.isFinite(max) && max > min ? max - min : 0;
}

/**
 * 채널 시각(초)에 해당하는 행 — 그 시각 이하의 마지막 행. 범위 밖은 양 끝으로 clamp.
 * 시각은 단조 증가가 기대값이라 이진 탐색한다(아니면 엔진이 소견으로 알려 온다).
 */
export function rowIndexAtSeconds(channel: InspectChannel, seconds: number): number {
  const times = channel.secondsFromOrigin;
  if (times.length === 0) {
    return 0;
  }
  let lo = 0;
  let hi = times.length - 1;
  if (seconds <= times[lo]) return lo;
  if (seconds >= times[hi]) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= seconds) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/**
 * 원본이 밝힌 task 문구.
 *
 * `source_metadata` 는 포맷마다 다른 opaque 묶음이라 모양을 확인하고 읽는다 — 없거나 문자열
 * 목록이 아니면 빈 목록이다(지어내지 않는다).
 */
export function episodeTasks(inspect: EpisodeInspect): readonly string[] {
  const tasks = inspect.sourceMetadata.tasks;
  if (!Array.isArray(tasks)) return [];
  return tasks.filter((task): task is string => typeof task === "string");
}

/**
 * 채널의 표본 간격 중앙값(초). 타임라인의 한 칸 이동에 쓴다 — 채널마다 rate 가 달라 하나의
 * `fps` 로 말할 수 없다. 양의 간격이 없으면 `null`.
 */
export function medianIntervalSeconds(channel: InspectChannel | undefined): number | null {
  const times = channel?.secondsFromOrigin ?? [];
  const gaps = times
    .slice(1)
    .map((time, index) => time - times[index])
    .filter((gap) => gap > 0)
    .sort((a, b) => a - b);
  if (gaps.length === 0) return null;
  const middle = Math.floor(gaps.length / 2);
  return gaps.length % 2 === 0 ? (gaps[middle - 1] + gaps[middle]) / 2 : gaps[middle];
}

/** 한 행의 값을 벡터로 — 범위 밖은 빈 배열. */
export function channelRow(channel: InspectChannel, row: number): readonly number[] {
  return channel.values?.[row] ?? [];
}

/** 파일 URL 이 지금 시각 기준으로 만료됐는지. 만료 직전(30초)도 만료로 본다 — 재생 중에 끊기지 않게. */
export function isMediaExpired(inspect: EpisodeInspect, nowMs: number): boolean {
  return inspect.mediaExpiresAtMs - nowMs <= MEDIA_EXPIRY_MARGIN_MS;
}

export const MEDIA_EXPIRY_MARGIN_MS = 30_000;
